import { Ref, computed, ref, shallowRef } from 'vue'

import { handleApiError } from '@/shared/utils/handleApiError'

export interface UseAsyncDataOptions<T> {
  onSuccess?: (data: T) => void
  /**
   * HTTP statuses the caller handles itself (e.g. an expected 404 shown as an
   * inline state). No error toast is shown for them; `error` is still set.
   */
  silentStatuses?: number[]
}

const isSilentError = (error: unknown, silentStatuses: number[] | undefined): boolean =>
  error instanceof Response && (silentStatuses?.includes(error.status) ?? false)

const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError'

/**
 * Wraps an async fetch in reactive data/error/status refs.
 *
 * Each `fetchData` call aborts the previous one (the signal is passed to `fn`,
 * which may hand it to the request) and only the latest call writes its
 * outcome, so a stale response can never overwrite a newer one.
 */
export function useAsyncData<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  options?: UseAsyncDataOptions<T> | ((data: T) => void)
) {
  // shallowRef so the response stays plain and doesn't leak reactive proxies.
  const data: Ref<T | null> = shallowRef(null)
  const error: Ref<any> = ref(null)
  const status: Ref<'idle' | 'pending' | 'error' | 'success'> = ref('idle')
  const pending = computed(() => status.value === 'pending')

  // Support both old callback style and new options object style
  const opts = typeof options === 'function' ? { onSuccess: options } : options

  let controller: AbortController | null = null
  let sequence = 0

  const fetchData = async () => {
    controller?.abort()
    controller = new AbortController()
    const current = ++sequence

    status.value = 'pending'
    try {
      const result = await fn(controller.signal)
      if (current !== sequence) return
      data.value = result
      opts?.onSuccess?.(data.value)
      status.value = 'success'
      return data
    } catch (e) {
      // A newer fetch has taken over; its outcome is the one that counts
      if (current !== sequence || isAbortError(e)) return
      error.value = e
      status.value = 'error'
      if (!isSilentError(e, opts?.silentStatuses)) handleApiError(e)
    }
  }

  return { data, error, pending, status, fetchData }
}
